import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { 
  BuildingOfficeIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ChartBarIcon,
  SignalIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    totalUsers: 0,
    totalClients: 0,
    totalSessions: 0,
    totalAgents: 0,
    totalMessages: 0,
    activeClients: 0,
    recentOrganizations: 0,
    verificationStats: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await api.getAdminDashboardStats(token);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const StatCard = ({ title, value, icon: Icon, color, description, onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${
        onClick ? 'cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(value)}</p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
      </div>
    </div>
  );

  const VerificationCard = ({ status, count, color, icon: Icon }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center">
        <Icon className={`h-5 w-5 ${color} mr-2`} />
        <div>
          <p className="text-sm font-medium text-gray-900 capitalize">{status}</p>
          <p className="text-lg font-bold text-gray-700">{count}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">System overview and management</p>
            </div>
          </div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
          <StatCard
            title="Organizations"
            value={stats.totalOrganizations}
            icon={BuildingOfficeIcon}
            color="bg-blue-500"
            description="Total registered organizations"
            onClick={() => navigate('/admin/organizations')}
          />
          <StatCard
            title="Users"
            value={stats.totalUsers}
            icon={UsersIcon}
            color="bg-green-500"
            description="Active user accounts"
            onClick={() => navigate('/admin/organizations/users')}
          />
          <StatCard
            title="WhatsApp Agents"
            value={stats.totalClients}
            icon={ChatBubbleLeftRightIcon}
            color="bg-whatsapp-500"
            description="Connected WhatsApp instances"
            onClick={() => navigate('/admin/organizations/whatsapp-agents')}
          />
          <StatCard
            title="Sessions"
            value={stats.totalSessions}
            icon={CalendarDaysIcon}
            color="bg-purple-500"
            description="WhatsApp chat sessions"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
          <StatCard
            title="Total Messages"
            value={stats.totalMessages}
            icon={ChartBarIcon}
            color="bg-indigo-500"
            description="Messages processed across all agents"
          />
          <StatCard
            title="Active Agents (24h)"
            value={stats.activeClients}
            icon={SignalIcon}
            color="bg-green-600"
            description="Agents with recent activity"
          />
          <StatCard
            title="New Organizations (7d)"
            value={stats.recentOrganizations}
            icon={CalendarDaysIcon}
            color="bg-orange-500"
            description="Organizations registered this week"
          />
        </div>

        {/* Verification Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 mb-6 lg:mb-8">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Organization Verification Status</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <VerificationCard
              status="verified"
              count={stats.verificationStats.verified || 0}
              color="text-green-600"
              icon={CheckCircleIcon}
            />
            <VerificationCard
              status="pending"
              count={stats.verificationStats.pending || 0}
              color="text-yellow-600"
              icon={ClockIcon}
            />
            <VerificationCard
              status="unverified"
              count={stats.verificationStats.unverified || 0}
              color="text-gray-600"
              icon={XCircleIcon}
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => window.location.href = '/admin/organizations'}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <BuildingOfficeIcon className="h-6 w-6 text-blue-600 mb-2" />
              <p className="font-medium text-gray-900">Manage Organizations</p>
              <p className="text-sm text-gray-500">View and verify organizations</p>
            </button>
            
            <button 
              onClick={() => window.location.href = '/admin/admins'}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <UsersIcon className="h-6 w-6 text-purple-600 mb-2" />
              <p className="font-medium text-gray-900">Admin Management</p>
              <p className="text-sm text-gray-500">Create and manage admin users</p>
            </button>
            
            <button 
              onClick={() => window.location.href = '/admin/system'}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <ChartBarIcon className="h-6 w-6 text-green-600 mb-2" />
              <p className="font-medium text-gray-900">System Analytics</p>
              <p className="text-sm text-gray-500">View detailed system metrics</p>
            </button>
            
            <button 
              onClick={() => window.location.href = '/admin/settings'}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <RocketLaunchIcon className="h-6 w-6 text-orange-600 mb-2" />
              <p className="font-medium text-gray-900">System Settings</p>
              <p className="text-sm text-gray-500">Configure system parameters</p>
            </button>
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
